import { useContext, useEffect, useRef } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";

/**
 * Asks before leaving a page with unsaved changes (CU-LS-M1-057).
 *
 * The app uses <BrowserRouter>, where react-router's useBlocker is not available, so this:
 * - wraps the router's navigator while there are unsaved changes (in-app links, navigate(), navigate(-1));
 * - catches the browser's Back button: one copy of the current history entry is pushed the first time the page has
 *   unsaved changes, so Back first lands on the same page, where the user is asked; "stay" pushes the copy again.
 *   At most one copy exists per page visit; a Back while there is nothing to save just uses it up;
 * - adds a beforeunload prompt (reload, closing the tab, typing a URL).
 */
const useUnsavedChangesGuard = (when, message = "You have unsaved changes. Leave without saving?") => {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const whenRef = useRef(when);
  const copyPushed = useRef(false);
  const leaving = useRef(false);
  whenRef.current = when;

  // Back button: lives for the whole page visit so the copy entry is tracked even while nothing is unsaved.
  useEffect(() => {
    const here = window.location.href;
    const hereState = window.history.state;
    const onPopState = (event) => {
      if (leaving.current) return;
      copyPushed.current = false; // Back used up the copy entry
      if (!whenRef.current) return;
      // eslint-disable-next-line no-alert
      if (window.confirm(message)) {
        leaving.current = true;
        if (window.location.href === here) window.history.go(-1);
      } else {
        // Capture before BrowserRouter observes the destination, preserving the mounted editor.
        event.stopImmediatePropagation();
        window.history.pushState(hereState, "", here);
        copyPushed.current = true;
      }
    };
    window.addEventListener("popstate", onPopState, true);
    return () => window.removeEventListener("popstate", onPopState, true);
  }, [message]);

  useEffect(() => {
    if (!when) return undefined;
    if (!copyPushed.current) {
      window.history.pushState(window.history.state, "", window.location.href);
      copyPushed.current = true;
    }
    const original = { push: navigator.push, replace: navigator.replace, go: navigator.go };
    const confirmThen =
      (fn) =>
      (...args) => {
        // eslint-disable-next-line no-alert
        if (window.confirm(message)) {
          leaving.current = true;
          fn(...args);
        }
      };
    navigator.push = confirmThen((...args) => original.push.apply(navigator, args));
    navigator.replace = confirmThen((...args) => original.replace.apply(navigator, args));
    // navigate(-n): already confirmed, so also step over the copy entry.
    navigator.go = confirmThen((delta) => {
      const extra = delta < 0 && copyPushed.current ? 1 : 0;
      copyPushed.current = false;
      original.go.call(navigator, delta - extra);
    });

    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      navigator.push = original.push;
      navigator.replace = original.replace;
      navigator.go = original.go;
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [when, message, navigator]);
};

export default useUnsavedChangesGuard;
